import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faSearchPlus } from '@fortawesome/free-solid-svg-icons';

const extractDominantColor = (imgSrc, callback) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 50;
      canvas.height = 50;
      ctx.drawImage(img, 0, 0, 50, 50);
      const data = ctx.getImageData(0, 0, 50, 50).data;
      let r = 0, g = 0, b = 0, count = 0;
      for (let i = 0; i < data.length; i += 16) {
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        count++;
      }
      if (count > 0) {
        callback(`${Math.round(r / count)}, ${Math.round(g / count)}, ${Math.round(b / count)}`);
      }
    } catch {
      callback('34, 197, 94');
    }
  };
  img.onerror = () => callback('34, 197, 94');
  img.src = imgSrc;
};

const slideVariants = {
  enter: (dir) => ({
    opacity: 0,
    x: dir * 60,
    scale: 0.97,
  }),
  center: {
    opacity: 1,
    x: 0,
    scale: 1,
  },
  exit: (dir) => ({
    opacity: 0,
    x: dir * -60,
    scale: 0.97,
  }),
};

const ProductGallery = ({
  images,
  productName,
  selectedImage,
  onImageSelect,
  onOpenLightbox,
  onDominantColor,
}) => {
  const dirRef = useRef(1);

  const handlePrev = (e) => {
    e.stopPropagation();
    dirRef.current = -1;
    onImageSelect(selectedImage === 0 ? images.length - 1 : selectedImage - 1);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    dirRef.current = 1;
    onImageSelect(selectedImage === images.length - 1 ? 0 : selectedImage + 1);
  };

  const handleThumbClick = (i) => {
    dirRef.current = i > selectedImage ? 1 : -1;
    onImageSelect(i);
  };

  useEffect(() => {
    if (images[selectedImage] && onDominantColor) {
      extractDominantColor(images[selectedImage], onDominantColor);
    }
  }, [selectedImage, images, onDominantColor]);

  if (!images || images.length === 0) return null;

  return (
    <div className="pd-gallery">
      {/* Main image */}
      <div
        className="pd-gallery__main"
        onClick={() => onOpenLightbox(selectedImage)}
      >
        <AnimatePresence mode="wait" custom={dirRef.current}>
          <motion.img
            key={selectedImage}
            src={images[selectedImage]}
            alt={`${productName || 'Product'} view ${selectedImage + 1}`}
            className="pd-gallery__image"
            custom={dirRef.current}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            draggable={false}
          />
        </AnimatePresence>

        {/* Zoom hint */}
        <div className="pd-gallery__zoom-hint">
          <FontAwesomeIcon icon={faSearchPlus} />
          <span>Click to zoom</span>
        </div>

        {/* Prev / Next arrows */}
        {images.length > 1 && (
          <>
            <button
              className="pd-gallery__arrow pd-gallery__arrow--prev"
              onClick={handlePrev}
              aria-label="Previous image"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              className="pd-gallery__arrow pd-gallery__arrow--next"
              onClick={handleNext}
              aria-label="Next image"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="pd-gallery__thumbs">
          {images.map((img, i) => (
            <button
              key={i}
              className={`pd-gallery__thumb${i === selectedImage ? ' is-active' : ''}`}
              onClick={() => handleThumbClick(i)}
              onDoubleClick={() => onOpenLightbox(i)}
              title={i === selectedImage ? 'Double-click to zoom' : `View image ${i + 1}`}
            >
              <img src={img} alt={`${productName || 'Product'} thumbnail ${i + 1}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
